import { ColorPicker, Tooltip } from 'antd';
import { CheckOutlined, DesktopOutlined, MoonOutlined, SunOutlined } from '@ant-design/icons';
import type { ReactNode } from 'react';

import { Badge } from '../../../components/ui';
import { Tile, TileBody, TileHeader } from '../../../components/shared';
import { useTheme } from '../../../components/ThemeProvider';
import type { CheDoNen } from '../../../components/ThemeProvider';
import { useT } from '../../../i18n';
import { cn } from '../../../lib/cn';
import { layMauNhan, MA_MAU_TUY_Y, MAU_NHAN, MUC_BO_GOC, TUY_CHINH_MAC_DINH } from '../../../styles/tuy-chinh';
import { CAC_CHE_DO_NEN, CHE_DO_NEN_MAC_DINH } from '../constants';
import { DongThietLap } from './DongThietLap';

const BIEU_TUONG_CHE_DO: Record<CheDoNen, ReactNode> = {
  auto: <DesktopOutlined />,
  light: <SunOutlined />,
  dark: <MoonOutlined />,
};

export function ThietLapCaNhan() {
  const { cheDo, setCheDo, tuyChinh, setTuyChinh } = useTheme();
  const { t } = useT();

  const nhanKemMacDinh = (khoa: Parameters<typeof t>[0], laMacDinh: boolean) =>
    laMacDinh ? t('hauTo.macDinh', { nhan: t(khoa) }) : t(khoa);

  const dungMauTuyY = tuyChinh.mauNhan === MA_MAU_TUY_Y;
  const mauDangDung = layMauNhan(tuyChinh);

  return (
    <Tile span="xl:col-span-8">
      <TileHeader
        tone="primary"
        title={t('tuyChinh.nhomCaNhan')}
        action={<Badge variant="neutral">{t('tuyChinh.chiLuuTrenMayNay')}</Badge>}
      />
      <TileBody>
        <DongThietLap
          nhan={t('tuyChinh.cheDoNen')}
          mo={t('tuyChinh.cheDoNenMo')}
          laMacDinh={cheDo === CHE_DO_NEN_MAC_DINH}
          onKhoiPhuc={() => setCheDo(CHE_DO_NEN_MAC_DINH)}
        >
          <div role="radiogroup" aria-label={t('tuyChinh.cheDoNen')} className="flex flex-wrap gap-2">
            {CAC_CHE_DO_NEN.map((c) => {
              const chon = cheDo === c.ma;
              return (
                <button
                  key={c.ma}
                  type="button"
                  role="radio"
                  aria-checked={chon}
                  onClick={() => setCheDo(c.ma)}
                  className={cn(
                    'flex h-20 w-24 flex-col items-center justify-center gap-1.5 rounded-lg border text-xs transition-colors',
                    chon
                      ? 'border-primary-600 bg-primary-50 text-primary-700'
                      : 'border-default text-content-secondary hover:bg-subtle',
                  )}
                >
                  <span aria-hidden className="text-xl leading-none">
                    {BIEU_TUONG_CHE_DO[c.ma]}
                  </span>
                  {t(c.khoa)}
                </button>
              );
            })}
          </div>
        </DongThietLap>

        <DongThietLap
          nhan={t('tuyChinh.mauNhan')}
          mo={t('tuyChinh.mauNhanMo')}
          laMacDinh={tuyChinh.mauNhan === TUY_CHINH_MAC_DINH.mauNhan}
          onKhoiPhuc={() => setTuyChinh({ ...tuyChinh, mauNhan: TUY_CHINH_MAC_DINH.mauNhan })}
        >
          <div role="radiogroup" aria-label={t('tuyChinh.mauNhan')} className="flex flex-wrap gap-2">
            {MAU_NHAN.map((mau) => {
              const chon = tuyChinh.mauNhan === mau.ma;
              const nhan = nhanKemMacDinh(mau.khoa, mau.ma === TUY_CHINH_MAC_DINH.mauNhan);
              return (
                <Tooltip key={mau.ma} title={nhan}>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={chon}
                    aria-label={nhan}
                    onClick={() => setTuyChinh({ ...tuyChinh, mauNhan: mau.ma })}
                    style={{ background: mau.base }}
                    className={cn(
                      'grid h-12 w-16 place-items-center rounded-lg text-white transition-transform hover:scale-105',
                      chon && 'ring-2 ring-offset-2 ring-offset-[var(--bg-elevated)]',
                    )}
                  >
                    {chon && <CheckOutlined aria-hidden />}
                  </button>
                </Tooltip>
              );
            })}
          </div>
          <div
            role="group"
            aria-label={t('tuyChinh.mauTuyY')}
            className={cn(
              'flex max-w-sm items-center justify-between gap-3 rounded-lg border px-3 py-2',
              dungMauTuyY ? 'border-primary-600 bg-primary-50' : 'border-default',
            )}
          >
            <span className="min-w-0 text-xs font-medium text-content-secondary">{t('tuyChinh.mauTuyY')}</span>
            <ColorPicker
              value={tuyChinh.mauTuyY ?? mauDangDung.base}
              format="hex"
              disabledAlpha
              showText
              aria-label={t('tuyChinh.mauTuyYNhan')}
              onChange={(c) =>
                setTuyChinh({
                  ...tuyChinh,
                  mauNhan: MA_MAU_TUY_Y,
                  mauTuyY: c.toHexString().toUpperCase(),
                })
              }
            />
          </div>
          {dungMauTuyY && mauDangDung.daChinh && (
            <p role="status" className="text-2xs text-warning-700">
              {t('tuyChinh.daLamDam', { ma: mauDangDung.base })}
            </p>
          )}
        </DongThietLap>

        <DongThietLap
          nhan={t('tuyChinh.boGoc')}
          mo={t('tuyChinh.boGocMo')}
          laMacDinh={tuyChinh.boGoc === TUY_CHINH_MAC_DINH.boGoc}
          onKhoiPhuc={() => setTuyChinh({ ...tuyChinh, boGoc: TUY_CHINH_MAC_DINH.boGoc })}
        >
          <div role="radiogroup" aria-label={t('tuyChinh.boGoc')} className="flex flex-wrap gap-2">
            {MUC_BO_GOC.map((m) => {
              const chon = tuyChinh.boGoc === m.ma;
              const nhan = nhanKemMacDinh(m.khoa, m.ma === TUY_CHINH_MAC_DINH.boGoc);
              return (
                <Tooltip key={m.ma} title={nhan}>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={chon}
                    aria-label={nhan}
                    onClick={() => setTuyChinh({ ...tuyChinh, boGoc: m.ma })}
                    className={cn(
                      'flex h-16 w-20 flex-col items-center justify-center gap-1.5 rounded-lg border transition-colors',
                      chon
                        ? 'border-primary-600 bg-primary-50 text-primary-700'
                        : 'border-default text-content-muted hover:bg-subtle',
                    )}
                  >
                    <span
                      aria-hidden
                      style={{
                        borderTopLeftRadius: m.px,
                        borderTopRightRadius: m.px,
                      }}
                      className={cn('h-5 w-8 border-x border-t', chon ? 'border-primary-600' : 'border-strong')}
                    />
                    <span className="text-2xs tabular-nums">{m.px}</span>
                  </button>
                </Tooltip>
              );
            })}
          </div>
        </DongThietLap>
      </TileBody>
    </Tile>
  );
}
